<script setup lang="ts">
  import { marked } from 'marked'
  import type { AssetStoryblok, ServiceSummaryStoryblok } from '@/storyblok/component-types-sb'
  import { toAssetImageProps } from '@/utils/storyblok/normalizers'
  import { cva } from 'class-variance-authority'

  const props = withDefaults(
    defineProps<{
      blok: ServiceSummaryStoryblok
      rowExpanded?: boolean
      expandable?: boolean
      logo?: string | AssetStoryblok | null
    }>(),
    {
      rowExpanded: false,
      expandable: false,
      logo: null
    }
  )

  const emits = defineEmits<{
    'update:expanded': []
  }>()

  const description = computed(() => {
    if (props.blok.full || !props.blok.description) return ''
    return marked(props.blok.description)
  })

  const expandSection = () => {
    emits('update:expanded')
  }
  const expandButtonLabel = computed(() =>
    props.rowExpanded ? `Collapse ${props.blok.title}` : `Expand ${props.blok.title}`
  )

  const logoImageProps = computed(() => {
    if (!props.logo) return { src: '', alt: 'Club logo' }
    if (typeof props.logo === 'string') return { src: props.logo, alt: 'Club logo' }
    const imageProps = toAssetImageProps(props.logo)
    return {
      src: imageProps.src || '',
      alt: imageProps.alt || 'Club logo'
    }
  })

  const showServiceClubIcon = computed(
    () =>
      typeof props.blok.service === 'string' &&
      ['PAY', 'COLLECTING_ABC_POINTS'].includes(props.blok.service) &&
      !!logoImageProps.value.src
  )

  const expandedClass = computed(() =>
    cva('shrink-0 text-bt-blue-dark', {
      variants: {
        expanded: {
          true: 'rotate-180',
          false: 'rotate-0'
        }
      },
      defaultVariants: {
        expanded: false
      }
    })({
      expanded: props.rowExpanded
    })
  )
</script>

<template>
  <div class="service-summary flex min-w-0 flex-col gap-2">
    <component
      :is="expandable ? 'button' : 'div'"
      :type="expandable ? 'button' : undefined"
      class="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-3"
      :class="expandable ? 'cursor-pointer bg-transparent p-0 text-left' : ''"
      :aria-label="expandable ? expandButtonLabel : undefined"
      :aria-expanded="expandable ? rowExpanded : undefined"
      @click="expandable && expandSection()"
    >
      <div class="flex min-w-0 items-center gap-2">
        <NuxtImg
          v-if="showServiceClubIcon"
          :src="logoImageProps.src"
          :alt="logoImageProps.alt"
          height="20"
          class="h-5 w-auto shrink-0 object-contain"
          data-test="club-summary-icon"
        />
        <p class="m-0 min-w-0 break-words text-subtitle-2">{{ blok.title }}</p>
      </div>
      <Icon
        v-if="expandable"
        name="common/chevron-down"
        size="md"
        :class="expandedClass"
      />
    </component>
    <div
      v-if="description"
      class="[&_a]:font-inherit whitespace-normal break-words text-secondary-light text-body-3 [&_a]:text-inherit [&_a]:underline [&_a]:underline-offset-2"
      v-html="description"
    />
  </div>
</template>
